import type { ActivityOf } from '@wyn/content';
import { BTN, type IlluminationSim, illuminationSim, SCRIPT_H, SCRIPT_W, STROKE_LIMIT } from '@wyn/game-core';
import { useRef } from 'react';
import { Stage } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { canvasPoint, useRealtime } from './useRealtime.ts';

interface Blot {
  x: number;
  y: number;
  r: number;
}

// Every mark here sits on a light page, so the colours are tuned for it rather than taken from the (dark) theme.
function draw(g: CanvasRenderingContext2D, sim: IlluminationSim, blots: Blot[]) {
  const pts = sim.guide, tol = sim.tolerance;
  g.fillStyle = '#e8dcc0'; g.fillRect(0, 0, SCRIPT_W, SCRIPT_H);
  g.lineCap = 'round'; g.lineJoin = 'round';
  g.strokeStyle = 'rgba(160,120,40,.35)'; g.lineWidth = tol * 1.6;
  g.beginPath(); for (const p of pts) g.lineTo(p.x, p.y); g.stroke();
  g.strokeStyle = '#b8902e'; g.lineWidth = 1.5; g.setLineDash([5, 5]);
  g.beginPath(); for (const p of pts) g.lineTo(p.x, p.y); g.stroke(); g.setLineDash([]);
  const first = pts[0], last = pts[pts.length - 1];
  if (first) { g.fillStyle = '#4f8a3f'; g.beginPath(); g.arc(first.x, first.y, 8, 0, 7); g.fill(); }
  if (last) { g.fillStyle = '#8a3a2a'; g.beginPath(); g.arc(last.x, last.y, 5, 0, 7); g.fill(); }
  g.strokeStyle = '#2a1a10'; g.lineWidth = 3.5;
  g.beginPath(); for (const p of sim.ink) g.lineTo(p.x, p.y); g.stroke();
  g.fillStyle = '#2a1a10';
  for (const b of blots) { g.beginPath(); g.arc(b.x, b.y, b.r, 0, 7); g.fill(); }
}

/** The Scriptorium: press on the green mark and trace the gilded guide in one stroke. */
export function IlluminationGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'illumination'>;
  const canvas = useRef<HTMLCanvasElement>(null);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);
  const pen = useRef({ x: 0, y: 0 });
  // Ink blots are decoration only: the simulation scores the line, not the blots.
  const blots = useRef<{ stroke: number; ink: number; list: Blot[] }>({ stroke: -1, ink: 0, list: [] });

  const rt = useRealtime<IlluminationSim>({
    create: () => illuminationSim(a, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
    pointer: () => pen.current,
    onFrame: (sim) => {
      const c = canvas.current;
      if (!c) return;
      if (!ctx.current) {
        const d = window.devicePixelRatio || 1;
        c.width = SCRIPT_W * d;
        c.height = SCRIPT_H * d;
        ctx.current = c.getContext('2d')!;
        ctx.current.setTransform(d, 0, 0, d, 0, 0);
      }
      const b = blots.current;
      if (b.stroke !== sim.stroke) Object.assign(b, { stroke: sim.stroke, ink: 0, list: [] });
      for (; b.ink < sim.ink.length; b.ink++) {
        const p = sim.ink[b.ink]!;
        if (b.ink > 0 && sim.lastDistance > sim.tolerance * 2 && Math.random() < 0.3)
          b.list.push({ x: p.x + (Math.random() - 0.5) * 6, y: p.y + (Math.random() - 0.5) * 6, r: 3 + Math.random() * 4 });
      }
      draw(ctx.current, sim, b.list);
    },
  });
  const sim = rt.sim;
  const hold = rt.holdProps(BTN.main);
  const track = (e: React.PointerEvent<HTMLCanvasElement>) => (pen.current = canvasPoint(e.currentTarget, e, SCRIPT_W));
  const traced = sim ? Math.round((sim.covered.filter(Boolean).length / Math.max(1, sim.covered.length)) * 100) : 0;
  const left = sim?.started ? Math.max(0, sim.timeLeft) : STROKE_LIMIT;

  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">Press on the green dot and trace along the gilded guide in one stroke. Lift when you reach the end. Stray too far and the ink blots.</p>
      <canvas
        ref={canvas}
        className="mx-auto block aspect-[34/24] w-full max-w-[340px] cursor-crosshair touch-none border border-rule"
        aria-label="The page and the gilded guide"
        onPointerDown={(e) => {
          track(e);
          hold.onPointerDown(e);
        }}
        onPointerMove={track}
        onPointerUp={hold.onPointerUp}
        onPointerCancel={hold.onPointerCancel}
        onContextMenu={hold.onContextMenu}
      />
      <div className="flex justify-between font-mono text-sm text-ink-dim">
        <span>Flourish {sim ? Math.min(sim.stroke + 1, sim.strokes) : 1}/{a.strokes} · Traced {traced}%</span>
        <span>{left.toFixed(1)}s</span>
      </div>
    </Stage>
  );
}
